import { router } from "@/shared/lib/router";
import { resetClient } from "@/shared/gql/apollo-client";
import { AUTH_ROUTE } from "@/shared/lib/routes";
import type { AuthUserQuery } from "@/shared/gql/graphql";
import * as Sentry from "@sentry/react";
import { resetAnalytics } from "@/shared/lib/analytics";

// Only allow internal paths to prevent open-redirect via ?redirect=https://evil.com
// or protocol-relative URLs (//evil.com).
export const safeRedirectPath = (path: string | undefined): string =>
  path && path.startsWith("/") && !path.startsWith("//") ? path : "/";

export const afterAuth = (redirect?: string) => {
  const safeRedirect = sanitizeRedirect(redirect);

  // External backend redirect (e.g. the OAuth /oauth/authorize consent handoff):
  // hand off with a full-page navigation and do NOTHING else. router.invalidate()
  // and resetClient() fire concurrent requests to the backend (the `me` query,
  // /sanctum/csrf-cookie) that race against — and clobber — the OAuth session the
  // consent page just wrote (authToken/authRequest), which makes the approve step
  // 403. The SPA is being torn down by this navigation anyway, so that work is both
  // pointless and harmful here.
  if (safeRedirect && isExternalBackendRedirect(safeRedirect)) {
    window.location.assign(safeRedirect);
    return;
  }

  clearAuthCache();
  router.invalidate();
  // External backend redirects are handled above; for in-app navigation use the
  // strict internal-path guard (also blocks protocol-relative //evil.com).
  router.navigate({ to: safeRedirectPath(redirect), replace: true });
  resetClient();
};

export const afterUnauth = () => {
  Sentry.setUser(null);
  resetAnalytics();
  clearAuthCache();
  router.invalidate();

  try {
    router.navigate({ to: AUTH_ROUTE, replace: true });
  } catch (error) {
    console.error("Failed to navigate after logout:", error);
  }
  resetClient();
};

// Redirects to the API host (e.g. /oauth/authorize) need a real page navigation,
// not router.navigate — TanStack Router only resolves in-app routes.
const isExternalBackendRedirect = (target: string): boolean => {
  if (!/^https?:\/\//i.test(target)) {
    return false;
  }

  try {
    const targetUrl = new URL(target);
    const backendUrl = new URL(import.meta.env.VITE_BACKEND_URL);
    return targetUrl.host === backendUrl.host;
  } catch {
    return false;
  }
};

// SPA-side defence against an attacker-controlled redirect query parameter:
// drop anything that's a foreign absolute URL. Relative paths pass through to
// router.navigate; backend URLs pass through to window.location.assign. Anything
// else (e.g. https://attacker.example.com) is replaced with undefined so the
// caller lands on "/".
const sanitizeRedirect = (target?: string): string | undefined => {
  if (!target) {
    return undefined;
  }
  if (!/^https?:\/\//i.test(target)) {
    return target;
  }
  return isExternalBackendRedirect(target) ? target : undefined;
};

type Me = NonNullable<AuthUserQuery["me"]>;

let cachedMe: Me | null = null;
let cacheTimestamp = 0;

// 60s matches typical session activity patterns — long enough to avoid
// redundant fetches during navigation, short enough to pick up role/company changes.
const CACHE_TTL = 60_000;

export const getCachedAuth = (): Me | null => {
  if (cachedMe && Date.now() - cacheTimestamp < CACHE_TTL) {
    return cachedMe;
  }
  return null;
};

export const setCachedAuth = (me: Me | null) => {
  cachedMe = me;
  cacheTimestamp = Date.now();
};

export const clearAuthCache = () => {
  cachedMe = null;
  cacheTimestamp = 0;
};
